import { useState, useEffect } from 'react';

import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import { useAuth } from '#globalContexts/AuthProvider';

import registerBackground from '../../../../assets/images/register-background.jpg';
import { getLoginErrorMessage, getMagicLinkErrorMessage } from '../../utils/errorMessages';

const LoginPage = () => {
	// Invite mails link here with ?email= so an expired invite button can be replaced by a fresh link.
	const [searchParams] = useSearchParams();
	const prefilledEmail = searchParams.get('email') ?? '';
	const [activeTab, setActiveTab] = useState(prefilledEmail ? 'magiclink' : 'password');
	const [formData, setFormData] = useState({ email: prefilledEmail, password: '' });
	const [isLoading, setIsLoading] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [error, setError] = useState('');
	const [showResendOption, setShowResendOption] = useState(false);
	const [resendSuccess, setResendSuccess] = useState('');
	const [magicLinkSent, setMagicLinkSent] = useState(false);
	const { user, signIn, signInWithMagicLink, resendVerification } = useAuth();
	const navigate = useNavigate();

	//Navigate to dashboard page if already logged in
	useEffect(() => {
		if (user) {
			navigate('/dashboard');
		}
	}, [user, navigate]);

	const handleFormInput = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		setError('');
		setResendSuccess('');
		setShowResendOption(false);
		setMagicLinkSent(false);
	};

	const handlePasswordSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		setError('');
		setResendSuccess('');
		setShowResendOption(false);

		const { error } = await signIn(formData.email, formData.password);

		if (error) {
			const { errorMessage, needsVerification } = getLoginErrorMessage(error);
			setError(errorMessage);
			setShowResendOption(needsVerification);
			setIsLoading(false);
		} else {
			navigate('/dashboard');
		}
	};

	const handleMagicLinkSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		setError('');
		setMagicLinkSent(false);

		const { error } = await signInWithMagicLink(formData.email);

		if (error) {
			const { errorMessage } = getMagicLinkErrorMessage(error);
			setError(errorMessage);
		} else {
			setMagicLinkSent(true);
		}
		setIsLoading(false);
	};

	const handleResendVerification = async () => {
		if (!formData.email) {
			setError('Bitte geben Sie Ihre E-Mail-Adresse ein');
			return;
		}

		setIsResending(true);
		setError('');
		setResendSuccess('');

		try {
			const { error } = await resendVerification(formData.email);

			if (error) {
				setError(error.message);
			} else {
				setResendSuccess(
					'Verifizierungs-E-Mail wurde erneut gesendet! Bitte überprüfen Sie Ihr Postfach (auch den Spam-Ordner).',
				);
				setShowResendOption(false);
			}
		} catch (err) {
			setError('Fehler beim Versenden der E-Mail');
		} finally {
			setIsResending(false);
		}
	};

	return (
		<div
			className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center relative"
			style={{
				backgroundImage: `url(${registerBackground})`,
			}}
		>
			{/* Overlay with blur effect */}
			<div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />

			<div className="relative z-10">
				<Card className="p-8 mx-4 w-full max-w-md">
					<div className="text-left mb-6">
						<h1 className="text-lg font-bold text-text mb-2">Willkommen zurück</h1>
						<p className="text-text">Melden Sie sich in Ihrem Konto an</p>
					</div>

					{/* Tab Navigation */}
					<div className="flex mb-6 border-b border-gray-200">
						<button
							type="button"
							onClick={() => handleTabChange('password')}
							className={`flex-1 py-3 text-sm font-medium transition-colors ${
								activeTab === 'password'
									? 'text-primary-700 border-b-2 border-primary-500'
									: 'text-bgBase-800 hover:text-text'
							}`}
						>
							Mit Passwort
						</button>
						<button
							type="button"
							onClick={() => handleTabChange('magiclink')}
							className={`flex-1 py-3 text-sm font-medium transition-colors ${
								activeTab === 'magiclink'
									? 'text-primary-700 border-b-2 border-primary-500'
									: 'text-bgBase-800 hover:text-text'
							}`}
						>
							Login ohne Passwort
						</button>
					</div>

					{error && (
						<div className="bg-red-50 border border-red-200 text-warning-700 px-4 py-3 rounded-lg mb-4">
							{error}
						</div>
					)}
					{resendSuccess && (
						<div className="bg-green-50 border border-green-200 text-success-700 px-4 py-3 rounded-lg mb-4">
							{resendSuccess}
						</div>
					)}

					{showResendOption && (
						<div className="bg-yellow-50 border border-yellow-200 text-caution-700 px-4 py-3 rounded-lg mb-4">
							<p className="mb-2 text-sm">E-Mail noch nicht bestätigt?</p>
							<Button variant="solid" size="sm" onClick={handleResendVerification} loading={isResending}>
								Bestätigungs-E-Mail erneut senden
							</Button>
						</div>
					)}

					{/* Magic Link Success Message */}
					{magicLinkSent && (
						<div className="bg-green-50 border border-green-200 text-success-700 px-4 py-3 rounded-lg mb-4">
							<p className="font-medium mb-1">Zugriffslink gesendet!</p>
							<p className="text-sm">
								Wir haben einen Anmeldelink an <strong>{formData.email}</strong> gesendet, wenn Sie mit
								dieser Adresse bei uns registriert sind. Bitte überprüfen Sie Ihr Postfach und klicken
								Sie auf den Link, um sich anzumelden.
							</p>
						</div>
					)}

					{/* Password Login Form */}
					{activeTab === 'password' && (
						<form onSubmit={handlePasswordSubmit}>
							<div className="mb-4">
								<TextInput
									type="email"
									placeholder="E-Mail-Adresse"
									name="email"
									value={formData.email}
									onChange={handleFormInput}
									required
								/>
							</div>

							<div className="mb-6">
								<TextInput
									type="password"
									placeholder="Passwort"
									name="password"
									value={formData.password}
									onChange={handleFormInput}
									required
								/>
							</div>

							<Button
								type="submit"
								variant="solid"
								size="md"
								fullWidth
								loading={isLoading}
								disabled={isResending}
							>
								Anmelden
							</Button>

							<div className="text-center mt-4">
								<Link
									to="/forgot-password"
									className="text-sm text-primary-700 hover:text-primary-700 font-medium"
								>
									Passwort vergessen?
								</Link>
							</div>
						</form>
					)}

					{/* Magic Link Login Form */}
					{activeTab === 'magiclink' && (
						<form onSubmit={handleMagicLinkSubmit}>
							<div className="mb-4">
								<p className="text-sm text-text mb-4">
									Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Anmelden.
								</p>
								<TextInput
									type="email"
									placeholder="E-Mail-Adresse"
									name="email"
									value={formData.email}
									onChange={handleFormInput}
									required
								/>
							</div>

							<Button
								type="submit"
								variant="solid"
								size="md"
								fullWidth
								loading={isLoading}
								disabled={magicLinkSent}
							>
								{magicLinkSent ? 'Link gesendet' : 'Zugriffslink senden'}
							</Button>

							{magicLinkSent && (
								<div className="text-center mt-4">
									<button
										type="button"
										onClick={() => setMagicLinkSent(false)}
										className="text-sm text-primary-700 hover:text-primary-700 font-medium"
									>
										Erneut senden
									</button>
								</div>
							)}
						</form>
					)}

					<div className="text-center mt-4">
						<span className="text-sm text-bgBase-800">Noch kein Konto? </span>
						<Link to="/register" className="text-sm text-primary-700 hover:text-primary-700 font-medium">
							Jetzt registrieren
						</Link>
					</div>
				</Card>
			</div>
		</div>
	);
};

export default LoginPage;
